import { createContext, useCallback, useContext, useState } from 'react';
import { IconCheck, IconAlert, IconX } from './Icons';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}

let _id = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback(id => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const show = useCallback((type, message, opts = {}) => {
    if (!message) return null;
    const id = ++_id;
    const duration = opts.duration ?? (type === 'error' ? 6000 : 3500);
    setToasts(t => {
      const next = [...t, { id, type, message: String(message), title: opts.title }];
      return next.length > 4 ? next.slice(next.length - 4) : next;
    });
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const value = {
    success: (msg, opts) => show('success', msg, opts),
    error:   (msg, opts) => show('error', msg, opts),
    info:    (msg, opts) => show('info', msg, opts),
    dismiss,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'fixed',
          bottom: 'calc(16px + env(safe-area-inset-bottom))',
          right: '16px',
          left: '16px',
          marginLeft: 'auto',
          maxWidth: '380px',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
      <style>{`
        @keyframes sp-toast-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

const PALETTE = {
  success: { color: 'var(--ok2)',  bg: 'rgba(90,158,114,0.14)', border: 'rgba(90,158,114,0.35)' },
  error:   { color: 'var(--err2)', bg: 'rgba(176,88,72,0.14)',  border: 'rgba(176,88,72,0.35)'  },
  info:    { color: 'var(--ink2)', bg: 'var(--bg-card)',        border: 'var(--border)'        },
};

function ToastItem({ toast, onDismiss }) {
  const p = PALETTE[toast.type] || PALETTE.info;
  const Icon = toast.type === 'success' ? IconCheck
             : toast.type === 'error'   ? IconAlert
             : null;
  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      style={{
        pointerEvents: 'auto',
        background: p.bg,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${p.border}`,
        borderRadius: 'var(--r-card)',
        boxShadow: 'var(--shadow-card)',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        animation: 'sp-toast-in 0.18s ease-out',
      }}
    >
      {Icon && (
        <span style={{ color: p.color, flexShrink: 0, marginTop: '1px' }}>
          <Icon className="w-4 h-4" />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <p style={{ fontSize: '11px', fontWeight: 700, color: p.color, marginBottom: '2px' }}>
            {toast.title}
          </p>
        )}
        <p style={{ fontSize: '12px', color: 'var(--ink)', lineHeight: 1.4, wordBreak: 'break-word' }}>
          {toast.message}
        </p>
      </div>
      <button
        onClick={onDismiss}
        aria-label="Zamknij"
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: 'none',
          color: 'var(--ink3)',
          cursor: 'pointer',
          padding: '2px',
          marginTop: '-2px',
          marginRight: '-2px',
        }}
      >
        <IconX className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
